import { z } from 'zod';
import {
  CatalogEditors,
  DerivedPolicy,
  GuestBooking,
  OrderConfirmation,
  OrderTiming,
  ReservationPayment,
  ShopFulfilment,
  TenantOfferPolicy,
  TenantStatus,
  TillConfig,
} from '@yourstore/domain';

/**
 * The operator wire, as this console understands it.
 *
 * It mirrors `apps/admin-api/src/wire.ts` by hand and on purpose. Apps do not
 * cross-import (dev.md §3: the dependency arrows run through `packages/`, and
 * that module's types come from `@yourstore/store`, which would drag Drizzle
 * and the `postgres` driver into a browser bundle). What the two files *do*
 * share is `@yourstore/domain` — `TenantStatus` is one enum rather than two
 * lookalikes, which matters because the whole lifecycle screen is built from
 * it and `authority: "live"` hangs off one of its members.
 *
 * Everything the console renders is parsed through these schemas first. On the
 * staff surface that rule exists because a malformed row would be written into
 * a mirror and repeated offline for as long as the tunnel lasts; here there is
 * no mirror, and the reason is different: this surface's screens are the
 * evidence an operator acts on — which keys are live, which tenant is `live`,
 * which deliveries failed — and a field that silently arrived as `undefined`
 * would render as an empty cell that reads like a fact.
 *
 * **What is deliberately absent is the point of §6.3.** There is no client, no
 * appointment, no settlement and no webhook *payload* in this file, because
 * there is none on the wire: `admin_api` holds no grant on those tables
 * (migration 0008) and the console must not even ask. If a schema here ever
 * grows a guest-shaped field, that is the bug.
 */

/* ------------------------------------------------------------------ */
/* The shared envelope                                                 */
/* ------------------------------------------------------------------ */

export const ErrorBody = z.object({
  error: z.object({
    code: z.enum([
      'invalid',
      'unauthenticated',
      'forbidden',
      'not_found',
      'conflict',
      'rate_limited',
      'unavailable',
      'unsupported',
    ]),
    message: z.string(),
    detail: z.record(z.string(), z.unknown()).optional(),
  }),
});
export type ErrorBody = z.infer<typeof ErrorBody>;

export const ModuleStatus = z.enum(['on', 'off', 'trial']);
export type ModuleStatus = z.infer<typeof ModuleStatus>;

export const KeyEnv = z.enum(['test', 'live']);
export type KeyEnv = z.infer<typeof KeyEnv>;

export const DeliveryStatus = z.enum(['queued', 'delivering', 'delivered', 'failed', 'exhausted']);
export type DeliveryStatus = z.infer<typeof DeliveryStatus>;

/* ------------------------------------------------------------------ */
/* The fleet                                                           */
/* ------------------------------------------------------------------ */

export const FleetTenant = z.object({
  tenant_id: z.string(),
  name: z.string(),
  timezone: z.string(),
  currency: z.string(),
  status: TenantStatus,
  plan: z.string(),
  created_at: z.string(),
  modules: z.array(z.string()),
  module_count: z.number(),
  active_keys: z.number(),
  revoked_keys: z.number(),
  webhook_endpoints: z.number(),
  webhook_failing: z.boolean(),
  last_activity_at: z.string().nullable(),
  /** Migration 0047's flag; defaulted so an older `admin-api` reads as "not protected". */
  protected: z.boolean().default(false),
});
export type FleetTenant = z.infer<typeof FleetTenant>;

export const FleetPage = z.object({
  tenants: z.array(FleetTenant),
  total: z.number(),
  limit: z.number(),
  offset: z.number(),
});
export type FleetPage = z.infer<typeof FleetPage>;

/* ------------------------------------------------------------------ */
/* One tenant                                                          */
/* ------------------------------------------------------------------ */

export const ModuleRow = z.object({
  module: z.string(),
  status: ModuleStatus,
  enabled_at: z.string().nullable(),
  label: z.string(),
  depends_on: z.array(z.string()),
});
export type ModuleRow = z.infer<typeof ModuleRow>;

/** Prefixes, scopes, status and last use. Never a hash, never a plaintext (K5). */
export const ApiKeyRow = z.object({
  key_id: z.string(),
  integration: z.string(),
  env: KeyEnv,
  prefix: z.string(),
  status: z.enum(['active', 'revoked']),
  active_slot: z.number().nullable(),
  scopes: z.array(z.string()),
  created_at: z.string(),
  last_used_at: z.string().nullable(),
  revoked_at: z.string().nullable(),
});
export type ApiKeyRow = z.infer<typeof ApiKeyRow>;

export const WebhookEndpoint = z.object({
  endpoint_id: z.string(),
  integration: z.string(),
  url: z.string(),
  /** The Secret Manager *reference*. The secret itself is never a column. */
  secret_ref: z.string(),
  signature_header: z.string(),
  event_types: z.array(z.string()),
  active: z.boolean(),
  failing: z.boolean(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type WebhookEndpoint = z.infer<typeof WebhookEndpoint>;

/**
 * A delivery attempt — and note what a delivery does **not** carry: its
 * payload. That is the one field on this whole surface that could hold a
 * guest's name and arrival time, and `admin-api` omits it by projection. The
 * console therefore cannot render it, which is the intended shape of the
 * ladder rather than an oversight to fix later.
 */
export const Delivery = z.object({
  delivery_id: z.string(),
  tenant_id: z.string(),
  event_id: z.string(),
  event_type: z.string(),
  occurred_at: z.string(),
  endpoint_id: z.string(),
  integration: z.string(),
  url: z.string(),
  status: DeliveryStatus,
  attempts: z.number(),
  next_attempt_at: z.string(),
  last_error: z.string().nullable(),
  last_status_code: z.number().nullable(),
  delivered_at: z.string().nullable(),
  updated_at: z.string(),
});
export type Delivery = z.infer<typeof Delivery>;

/* ------------------------------------------------------------------ */
/* Domains (spec §6.4.2)                                               */
/* ------------------------------------------------------------------ */

export const DomainStatus = z.enum(['pending_dns', 'issuing', 'live']);
export type DomainStatus = z.infer<typeof DomainStatus>;

export const DomainKind = z.enum(['default', 'custom']);
export type DomainKind = z.infer<typeof DomainKind>;

/**
 * A hostname a tenant is served on.
 *
 * `served` is the server's own derivation, not something this file computes:
 * `app.tenant_for_host` (migration 0010) resolves `live` and nothing else, so
 * "is this business's website up?" has one answer and it is not assembled twice.
 * The console renders it and does not second-guess it — the same discipline the
 * rest of this file follows about `authority`.
 */
export const TenantDomain = z.object({
  hostname: z.string(),
  tenant_id: z.string(),
  kind: DomainKind,
  status: DomainStatus,
  served: z.boolean(),
  verified_at: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type TenantDomain = z.infer<typeof TenantDomain>;

export const DomainList = z.object({
  tenant_id: z.string(),
  domains: z.array(TenantDomain),
  note: z.string(),
});
export type DomainList = z.infer<typeof DomainList>;

export const DomainChanged = TenantDomain.extend({
  created: z.boolean().optional(),
  previous_status: DomainStatus.optional(),
  meaning: z.string(),
  note: z.string(),
});
export type DomainChanged = z.infer<typeof DomainChanged>;

/* ------------------------------------------------------------------ */
/* Branding — the theme editor (spec §6.4.3)                           */
/* ------------------------------------------------------------------ */

/**
 * The **theme contract, served as data**.
 *
 * This is the one place the console deliberately does not mirror a shape by
 * hand. `admin-api` sends the token table, the three presets with their whole
 * token sets, the block registry with its slot shapes, the sanitizer's
 * allowlist and every limit — and the editor builds its controls from that.
 *
 * The reason is the same one that put `MODULES` in `@yourstore/domain`: the
 * authority is the site bundle (`apps/site/src/theme/contract.ts` and
 * `sections/registry.ts`), apps do not cross-import, and a third hand-written
 * copy in this file would be a third thing to keep in step with a versioned
 * interface. There is one duplicate — `apps/admin-api/src/branding/contract.ts`,
 * which says at length why it exists and where it should end up — and the
 * console reads it over the wire instead of making a second.
 *
 * So these schemas describe the *envelope*, not the vocabulary: they say "a
 * token has a name, a type and a group", never which tokens exist. A service
 * that learns a new token is a console that renders a new control on the next
 * page load, with no rebuild.
 */
export const TokenType = z.enum(['color', 'length', 'number', 'font', 'text', 'url']);
export type TokenType = z.infer<typeof TokenType>;

export const TokenGroup = z.enum(['color', 'type', 'shape', 'space', 'imagery']);
export type TokenGroup = z.infer<typeof TokenGroup>;

export const ContractToken = z.object({
  name: z.string(),
  type: TokenType,
  label: z.string(),
  group: TokenGroup,
  css_variable: z.string(),
  hint: z.string(),
  /**
   * **The token's closed set, when it has one** — `masthead.layout` is one of
   * `left · centered · overlay` and nothing else, and the editor renders a
   * select for it instead of a text box.
   *
   * The envelope again, not the vocabulary: the console never learns *which*
   * tokens are choices, only that a token which arrives with a non-empty list
   * gets the other control. Defaulted like `repeatable` and `token_follows`, so
   * a console pointed at a service that predates the field draws the boxes it
   * always drew rather than failing to parse the payload.
   */
  choices: z.array(z.string()).catch([]).default([]),
});
export type ContractToken = z.infer<typeof ContractToken>;

export const ContractPreset = z.object({
  name: z.string(),
  blurb: z.string(),
  tokens: z.record(z.string(), z.string()),
});
export type ContractPreset = z.infer<typeof ContractPreset>;

/* ------------------------------------------------------------------ */
/* The two contract additions of the personality wave                  */
/* ------------------------------------------------------------------ */

/**
 * The named motion option — one reveal style, off by default
 * (`docs/tenant-site-offering.md`; `apps/site/src/theme/motion.tsx`).
 *
 * A *list of names with blurbs*, exactly like the presets, and deliberately not
 * a set of numeric fields. The console renders a radio group from it and has
 * nowhere to put a duration, which is the whole difference between a named
 * option and arbitrary animation config.
 */
export const MotionVocabulary = z.object({
  default: z.string(),
  options: z.array(z.object({ name: z.string(), blurb: z.string() })),
  note: z.string(),
});
export type MotionVocabulary = z.infer<typeof MotionVocabulary>;

export const StaffFieldKind = z.enum(['color', 'image_url', 'choice']);
export type StaffFieldKind = z.infer<typeof StaffFieldKind>;

/**
 * One field of the staff PWA's named set, in the words the server sent.
 *
 * The console owns no copy of this list — the same discipline the guest tab
 * follows, and the same reason: the *definition* lives in `packages/domain`
 * (`StaffBranding`, `STAFF_BRANDING_FIELDS`), `apps/admin-api` adds the labels,
 * and a table in a React component would be the third place a field could be
 * forgotten. A service that learns an eighth named field is a console that
 * renders an eighth control on the next page load.
 */
export const StaffField = z.object({
  name: z.string(),
  kind: StaffFieldKind,
  label: z.string(),
  hint: z.string(),
  options: z
    .array(z.object({ value: z.string(), label: z.string(), blurb: z.string() }))
    .optional(),
});
export type StaffField = z.infer<typeof StaffField>;

export const StaffVocabulary = z.object({
  note: z.string(),
  fields: z.array(StaffField),
  derived: z.object({ accent_soft: z.string() }),
});
export type StaffVocabulary = z.infer<typeof StaffVocabulary>;

/**
 * The stored staff document. A loose string map on purpose: the *server* owns
 * which keys are legal and refuses the rest at the door, and a schema here that
 * listed them would be the copy this file just said it does not keep.
 */
export const StaffBrandingDocument = z.record(z.string(), z.string());
export type StaffBrandingDocument = z.infer<typeof StaffBrandingDocument>;

export const SlotKind = z.enum([
  'text',
  'prose',
  'richtext',
  'enum',
  'flag',
  'rows',
  'images',
  'image_url',
  /** The `faq` block's question/answer pairs. */
  'qa',
  /** A list of short strings — the `inquiry` block's occasions. */
  'strings',
  /** A bounded integer — `days_shown`, `default_nights`. */
  'number',
  /**
   * The two list shapes the editor has no control for yet (`features`, and
   * `panels` since the story's zigzag rows). Named here so a contract that
   * carries one PARSES — the alternative was the whole Branding card dying
   * with "a shape this console does not know" the day the story block gained
   * its rows. The editor shows them as kept-as-stored.
   */
  'features',
  'panels',
  /**
   * `{ label, url }` rows that leave the site — the affiliate or partner
   * address a `shop`, `showcase` or `footer` band may carry
   * (`docs/lovespa-gaps-plan.md` C1). Editable here, unlike the two above:
   * two text boxes are the whole control, and the address is the one thing on
   * a page an owner is most likely to want to change herself.
   */
  'links',
]);
export type SlotKind = z.infer<typeof SlotKind>;

/**
 * **Localized tenant content, as the contract carries it**: a bare string, or a
 * map keyed by language (`{ en: 'Questions', es: 'Preguntas' }`).
 *
 * A nav label and a content page's title are both this shape
 * (`packages/theme/src/content.ts`, `pages.ts`), and the console **never
 * translates one** — it edits whichever languages the tenant wrote and keeps
 * the shape it found. The bare string is not a legacy spelling to be migrated:
 * a business with one language has one title, and rewriting it as `{ en: … }`
 * on the way through a publish would make the document say something the
 * operator did not.
 */
export const LocalizedText = z.union([z.string(), z.record(z.string(), z.string())]);
export type LocalizedText = z.infer<typeof LocalizedText>;

/**
 * **What a content page's slug and title may be** (`docs/site-section-system.md`
 * §9.6 decision 5; `packages/theme/src/content.ts`).
 *
 * Served rather than known, for this file's standing reason — the alphabet, the
 * ceiling and the reserved list are one rule enforced by the write path, and a
 * console that re-typed the pattern would be a second rule that could drift
 * from it. `slug_pattern` arrives as a regex *source* because JSON has no
 * regular expressions.
 *
 * Defaulted to a shape that offers **nothing**: `pages_max: 0` is how a console
 * says "this service has not told me it serves content pages", and the panel
 * does not render. That is the honest default in this direction — a service
 * that predates the fourth page shape answers `400` to a document carrying
 * `pages`, so offering the control would be offering a refusal.
 */
export const ContentPageLimits = z.object({
  slug_pattern: z.string().catch('').default(''),
  slug_max: z.number().catch(0).default(0),
  pages_max: z.number().catch(0).default(0),
  title_max: z.number().catch(0).default(0),
  sections_max: z.number().catch(0).default(0),
  reserved: z.array(z.string()).catch([]).default([]),
});
export type ContentPageLimits = z.infer<typeof ContentPageLimits>;

const NO_CONTENT_PAGES: ContentPageLimits = {
  slug_pattern: '',
  slug_max: 0,
  pages_max: 0,
  title_max: 0,
  sections_max: 0,
  reserved: [],
};

export const ContractSlot = z.object({
  name: z.string(),
  kind: SlotKind,
  label: z.string(),
  max: z.number(),
  fallback: z.string().optional(),
  options: z.array(z.string()).optional(),
  hint: z.string().optional(),
  /** A former name the slot still answers to (`variant` was `layout`). */
  alias: z.string().optional(),
});
export type ContractSlot = z.infer<typeof ContractSlot>;

export const ContractSection = z.object({
  type: z.string(),
  label: z.string(),
  why: z.string(),
  /**
   * **The pages this block may appear on** (`docs/site-section-system.md` §9.2:
   * "every section type declares the pages it may appear on… the console offers
   * a type only on the pages it lists").
   *
   * Defensively defaulted to **empty**, and empty is read as "this service has
   * not placed its blocks yet — offer it anywhere", which is exactly the
   * behaviour this console had before §9. The alternative default (`['home']`)
   * would have a console against an older service quietly hide blocks that
   * service is perfectly willing to publish.
   */
  pages: z.array(z.string()).catch([]).default([]),
  slots: z.array(ContractSlot),
  /**
   * **May a page carry more than one of these?** (`gallery` alone, today.)
   *
   * Additive and defensively defaulted: a console deployed against a service
   * that predates the flag reads `false` and offers one of each block, which is
   * exactly the behaviour it had before collections existed.
   */
  repeatable: z.boolean().catch(false).default(false),
});
export type ContractSection = z.infer<typeof ContractSection>;

/**
 * A page template as served (`docs/site-section-system.md` §4.3): a document
 * to start from, a shopping list of the photographs to bring, and the client
 * it is built against. Defensively defaulted like `repeatable`: a console
 * against a service that predates templates offers none.
 */
export const PageTemplate = z.object({
  name: z.string(),
  label: z.string(),
  vertical: z.string(),
  client: z.string(),
  blurb: z.string(),
  document: z.object({
    preset: z.string(),
    tokens: z.record(z.string(), z.string()).optional(),
    site_title: z.string().optional(),
    tagline: z.string().optional(),
    sections: z.array(
      z.object({ type: z.string(), id: z.string(), slots: z.record(z.string(), z.unknown()) }),
    ),
    /**
     * The template's content pages, keyed by slug — where the FAQ band went
     * when `HOME_GRANDFATHERED` was emptied (`packages/theme/src/templates.ts`).
     * Defaulted like everything else additive: a console against a service
     * whose templates predate the fourth page shape copies none.
     */
    pages: z
      .record(
        z.string(),
        z.object({
          title: LocalizedText.catch(''),
          sections: z
            .array(
              z.object({
                type: z.string(),
                id: z.string().optional(),
                slots: z.record(z.string(), z.unknown()).default({}),
              }),
            )
            .catch([])
            .default([]),
        }),
      )
      .catch({})
      .default({}),
    nav: z.array(z.object({ label: LocalizedText, target: z.string() })).optional(),
  }),
  shopping: z.array(
    z.object({ where: z.string(), ratio: z.string(), count: z.number(), note: z.string() }),
  ),
});
export type PageTemplate = z.infer<typeof PageTemplate>;

export const ThemeContract = z.object({
  version: z.number(),
  source: z.array(z.string()),
  source_note: z.string(),
  tokens: z.array(ContractToken),
  token_groups: z.array(z.object({ key: TokenGroup, label: z.string() })),
  /**
   * Tokens that follow another unless set themselves (`color.accent-2` →
   * `color.accent`), so the preview resolves a row the way the bundle does.
   * Defensively defaulted like `repeatable`: a console against a service
   * that predates the rule previews without it.
   */
  token_follows: z
    .array(z.object({ follower: z.string(), leader: z.string() }))
    .catch([])
    .default([]),
  templates: z.array(PageTemplate).catch([]).default([]),
  presets: z.array(ContractPreset),
  /**
   * The four page shapes a section may be placed on, and **which one this
   * console is arranging**. `branding.sections` is the home page's arrangement
   * today (§9.7 phase 7 is the second one), and the service refuses a block
   * placed on a page it does not list — so the editor filters its list by the
   * same field rather than discovering the rule as a `400`.
   *
   * Defaulted to `'home'` rather than to `''`: a service that predates the
   * field is one whose whole registry was the home page's, which is what
   * `'home'` means. The filter then reads every block's empty `pages` as
   * "offered anywhere" and nothing disappears.
   */
  page_kinds: z.array(z.string()).catch([]).default([]),
  console_page: z.string().catch('home').default('home'),
  console_page_note: z.string().catch('').default(''),
  /**
   * **Blocks the home page still takes although §9.3 places them elsewhere** —
   * `faq`, today, because every document that exists carries one there and the
   * content page that is its real home is §9.7 phase 7. They are *not* offered
   * in the chooser (that list is `pages`, and this deliberately does not widen
   * it), so this exists for one thing: telling a legal survivor in a stored
   * document from a block that is genuinely in the wrong place. Defaulted to
   * empty — a service that has not said so grandfathers nothing.
   */
  home_grandfathered: z.array(z.string()).catch([]).default([]),
  /**
   * The content page's own rules — the slug alphabet, the ceilings and the
   * slugs a route already owns. See `ContentPageLimits`: absent means this
   * service serves no fourth page shape and the Pages panel does not render.
   */
  content_pages: ContentPageLimits.catch(NO_CONTENT_PAGES).default(NO_CONTENT_PAGES),
  sections: z.array(ContractSection),
  default_arrangement: z.array(z.string()),
  /** `booking`. The bundle mounts the flow regardless, so the editor pins it. */
  pinned_section: z.string(),
  pinned_note: z.string(),
  sanitizer: z.object({
    allowed_tags: z.array(z.string()),
    allowed_attributes: z.record(z.string(), z.array(z.string())),
    allowed_link_schemes: z.array(z.string()),
    note: z.string(),
  }),
  limits: z.object({
    document_bytes: z.number(),
    custom_css_bytes: z.number(),
    history: z.number(),
  }),
  /**
   * The named motion option. **Additive and defaulted**, because a console can
   * be newer than the service it is pointed at during a rollout, and a card that
   * refused to render because one vocabulary block was missing would be a
   * console outage over a flourish.
   */
  motion: MotionVocabulary.catch({
    default: 'none',
    options: [],
    note: '',
  }).default({ default: 'none', options: [], note: '' }),
  /** The staff PWA's named set — the second contract on this payload. */
  staff: StaffVocabulary.catch({ note: '', fields: [], derived: { accent_soft: '' } }).default({
    note: '',
    fields: [],
    derived: { accent_soft: '' },
  }),
});
export type ThemeContract = z.infer<typeof ThemeContract>;

export const BrandingSection = z.object({
  type: z.string(),
  id: z.string().optional(),
  slots: z.record(z.string(), z.unknown()).default({}),
});
export type BrandingSection = z.infer<typeof BrandingSection>;

/**
 * One content page, as it sits in `branding.pages[<slug>]` — a localized title
 * and an arrangement out of the same registry the home page is built from
 * (`packages/theme/src/content.ts`, `ContentPageDocument`).
 *
 * Tolerant in both fields on purpose: a title this console cannot read and a
 * block it has no control for are both things it must carry through a publish
 * untouched rather than drop, which is the rule the whole editor runs on.
 */
export const ContentPage = z.object({
  title: LocalizedText.catch(''),
  sections: z.array(BrandingSection).catch([]).default([]),
});
export type ContentPage = z.infer<typeof ContentPage>;

/** One nav entry. The label is localized; the target grammar is the theme's. */
export const NavEntry = z.object({ label: LocalizedText.catch(''), target: z.string() });
export type NavEntry = z.infer<typeof NavEntry>;

/** Every field optional: `{}` is what a tenant who has published nothing has. */
export const BrandingDocument = z.object({
  contract_version: z.number().optional(),
  preset: z.string().optional(),
  tokens: z.record(z.string(), z.string()).optional(),
  sections: z.array(BrandingSection).optional(),
  site_title: z.string().optional(),
  tagline: z.string().optional(),
  custom_css: z.string().optional(),
  /** The named motion option. Absent means the default, `'none'`. */
  motion: z.string().optional(),
  /**
   * **The content pages, keyed by slug** — the fourth page shape
   * (`docs/site-section-system.md` §9.6 decision 5).
   *
   * Absent, never empty, exactly as the server stores it: a tenant with no
   * content pages stores byte-for-byte the document they stored before phase 7.
   */
  pages: z.record(z.string(), ContentPage).optional(),
  /**
   * The nav bar. **It is parsed here because a key this schema does not name is
   * a key the console silently deletes**: `BrandingDocument.parse` strips what
   * it does not know, `toDocument` rebuilds the body from the draft, and a bar
   * a tenant configured would leave on the next publish. Now that a nav entry
   * can point at a content page (`page:<slug>`), that would have taken the only
   * link to a page the same publish had just created.
   */
  nav: z.array(NavEntry).optional(),
  /**
   * The per-category blurbs, carried opaquely for the reason above and edited
   * nowhere in this console: there is no control for them yet, and "no control"
   * must mean *kept as stored* rather than *dropped on the next publish*.
   */
  categories: z.record(z.string(), z.unknown()).optional(),
});
export type BrandingDocument = z.infer<typeof BrandingDocument>;

export const BrandingHistoryRow = z.object({
  version: z.number(),
  at: z.string(),
  by: z.string(),
  note: z.string(),
  preset: z.string(),
});
export type BrandingHistoryRow = z.infer<typeof BrandingHistoryRow>;

const BrandingState = z.object({
  tenant_id: z.string(),
  branding: BrandingDocument,
  /**
   * The staff PWA's named set, or `null` for a tenant that has none — which is
   * every tenant but one, and is the state that renders the staff app exactly
   * as it ships (`docs/lovespa-personality.md`).
   *
   * `null` rather than an absent key, because the console has to be able to
   * tell "no staff branding" from "staff branding with everything at default" —
   * and the second of those is not a state that exists, since every field in the
   * set is a choice.
   */
  branding_staff: StaffBrandingDocument.nullable().catch(null).default(null),
  /** The wash the PWA derives from the accent, so the preview draws the same one. */
  branding_staff_accent_soft: z.string().nullable().catch(null).default(null),
  /** 0 means "never published" — a legitimate state, not a missing row. */
  branding_version: z.number(),
  published_at: z.string().nullable(),
  published_by: z.string().nullable(),
  publish_note: z.string(),
  custom_css_contract_version: z.number().nullable(),
  history: z.array(BrandingHistoryRow),
});

/** One day of the tenant's real week, which the preview may honestly paint. */
export const PreviewDay = z.object({
  weekday: z.number(),
  closed: z.boolean(),
  windows: z.array(z.object({ start: z.string(), end: z.string() })),
});
export type PreviewDay = z.infer<typeof PreviewDay>;

export const BrandingRead = BrandingState.extend({
  tenant: z.object({ name: z.string(), status: z.string(), timezone: z.string() }),
  /**
   * What the preview pane is allowed to be honest about.
   *
   * `catalog` is **always null** and the type says so. The operator console
   * holds no grant on `items` (migration 0008; `no-data-browser.test.ts` asserts
   * the `42501`), so §6.4.3's "live preview against the tenant's real catalog"
   * cannot be served here without breaking §6.3's "never a data browser". The
   * name and the week are real; the services are placeholders and the note says
   * so out loud.
   */
  preview: z.object({
    name: z.string(),
    hours: z.array(PreviewDay).catch([]),
    catalog: z.null(),
    catalog_note: z.string(),
  }),
  contract: ThemeContract,
  note: z.string(),
  /**
   * The engine's rules for this tenant (GS2), so the editor can badge a
   * `policies` row the engine overrides. Additive and tolerant: a console
   * ahead of its server reads `[]` and badges nothing, which is the state of
   * every tenant with no rules anyway.
   */
  policy_rules: z.array(DerivedPolicy).catch([]).default([]),
});
export type BrandingRead = z.infer<typeof BrandingRead>;

/** What the sanitizer did to one content zone, per zone, in the server's words. */
export const ZoneReport = z.object({
  path: z.string(),
  changed: z.boolean(),
  removed: z.array(z.string()),
  before_bytes: z.number(),
  after_bytes: z.number(),
});
export type ZoneReport = z.infer<typeof ZoneReport>;

export const BrandingPublished = BrandingState.extend({
  zones: z.array(ZoneReport),
  warnings: z.array(z.string()),
  document_bytes: z.number(),
  effective: z.string(),
});
export type BrandingPublished = z.infer<typeof BrandingPublished>;

export const BrandingRolledBack = BrandingState.extend({
  restored_from: z.number(),
  effective: z.string(),
});
export type BrandingRolledBack = z.infer<typeof BrandingRolledBack>;

export const AuditEntry = z.object({
  audit_id: z.string(),
  /** Null for genuinely cross-tenant work — a fleet listing (spec §10.9). */
  tenant_id: z.string().nullable(),
  actor: z.string(),
  action: z.string(),
  target: z.string(),
  detail: z.record(z.string(), z.unknown()),
  at: z.string(),
  /** The business's name, on the cross-tenant log; absent on older servers and the tenant read. */
  tenant_name: z.string().nullable().optional(),
});
export type AuditEntry = z.infer<typeof AuditEntry>;

export const TenantDetail = z.object({
  tenant: z.object({
    tenant_id: z.string(),
    name: z.string(),
    timezone: z.string(),
    currency: z.string(),
    status: TenantStatus,
    plan: z.string(),
    /** Derived, never configured: `live` only when the status is (spec §4.4). */
    authority: z.enum(['live', 'approximate']),
    /**
     * Migration 0047's flag — a real business, and nothing may wipe it.
     * Defaulted rather than required so a console deployed ahead of its
     * `admin-api` reads an older answer as "not protected" instead of failing
     * the whole page; the server is the one that refuses the wipe either way.
     */
    protected: z.boolean().default(false),
  }),
  config: z
    .object({
      catalog_version: z.number(),
      hours: z.unknown(),
      config: z.unknown(),
    })
    .nullable(),
  modules: z.array(ModuleRow),
  keys: z.array(ApiKeyRow),
  webhook_endpoints: z.array(WebhookEndpoint),
  recent_deliveries: z.array(Delivery),
  recent_audit: z.array(AuditEntry),
});
export type TenantDetail = z.infer<typeof TenantDetail>;

/* ------------------------------------------------------------------ */
/* Health and the activity log                                         */
/* ------------------------------------------------------------------ */

/**
 * Per-tenant health (docs/status-2026-09-21.md §5). Counts and instants,
 * each block with its `as_of`; the verdict and its reasons are the server's
 * (`repos/health.ts` in the store — the rule is written once, there) and the
 * console renders them. `reasons` is a list of strings rather than an enum so
 * a reason a newer server names renders as its token instead of failing the
 * parse; `reasonLabel` in `ui/business/activity/next.ts` words the current nine.
 */
export const HealthVerdict = z.enum(['quiet', 'attention', 'failing']);
export type HealthVerdict = z.infer<typeof HealthVerdict>;

const QueueHealth = z.object({
  as_of: z.string(),
  window_hours: z.number(),
  queued: z.number(),
  sent: z.number(),
  failed: z.number(),
  exhausted: z.number(),
  oldest_queued_at: z.string().nullable(),
});

export const TenantHealth = z.object({
  tenant_id: z.string(),
  as_of: z.string(),
  verdict: HealthVerdict,
  reasons: z.array(z.string()),
  site: z.object({
    as_of: z.string(),
    domains: z.number(),
    hostname: z.string().nullable(),
    kind: DomainKind.nullable(),
    status: DomainStatus.nullable(),
    served: z.boolean(),
    verified_at: z.string().nullable(),
    updated_at: z.string().nullable(),
  }),
  texts: QueueHealth,
  webhooks: QueueHealth.extend({
    newest_exhausted_at: z.string().nullable(),
    endpoint_failing: z.boolean(),
    active_endpoints: z.number(),
  }),
  requests: z.object({
    as_of: z.string(),
    waiting: z.number(),
    overdue: z.number(),
    oldest_overdue_at: z.string().nullable(),
  }),
  book: z.object({
    as_of: z.string(),
    last_booking_at: z.string().nullable(),
    next_seven_days: z.number(),
  }),
  key: z.object({
    as_of: z.string(),
    present: z.boolean(),
    integration: z.string().nullable(),
    prefix: z.string().nullable(),
    last_seen_at: z.string().nullable(),
  }),
});
export type TenantHealth = z.infer<typeof TenantHealth>;

export const FleetHealth = z.object({ health: z.array(TenantHealth), note: z.string() });
export type FleetHealth = z.infer<typeof FleetHealth>;

export const TenantHealthRead = TenantHealth.extend({ note: z.string() });
export type TenantHealthRead = z.infer<typeof TenantHealthRead>;

/**
 * A line of the activity log. `summary`, never `detail`: the server has
 * reduced the row to what a diff needs, and there is no field on this shape a
 * guest could be put back into.
 */
export const ActivityEntry = z.object({
  audit_id: z.string(),
  actor: z.string(),
  action: z.string(),
  target: z.string(),
  summary: z.record(z.string(), z.unknown()),
  at: z.string(),
});
export type ActivityEntry = z.infer<typeof ActivityEntry>;

export const ActivityPage = z.object({
  tenant_id: z.string(),
  entries: z.array(ActivityEntry),
  limit: z.number(),
  /** The cursor for the next page, or null when this one was short. */
  next_before: z.string().nullable(),
  note: z.string(),
});
export type ActivityPage = z.infer<typeof ActivityPage>;

/* ------------------------------------------------------------------ */
/* The writes                                                          */
/* ------------------------------------------------------------------ */

export const TenantCreated = z.object({
  tenant_id: z.string(),
  name: z.string(),
  timezone: z.string(),
  currency: z.string(),
  status: TenantStatus,
  plan: z.string(),
  modules: z.array(z.string()),
  next: z.string(),
});
export type TenantCreated = z.infer<typeof TenantCreated>;

export const StatusChanged = z.object({
  tenant_id: z.string(),
  previous_status: TenantStatus,
  status: TenantStatus,
  authority: z.enum(['live', 'approximate']),
  /** The key cache is 15 s (spec §5.2). Stated, not rounded to "instant". */
  effective_within_seconds: z.number(),
});
export type StatusChanged = z.infer<typeof StatusChanged>;

/**
 * **"See what she sees"** — the answer to `POST …/support-session`: a
 * fifteen-minute, read-only staff seat for one tenant, minted after its audit
 * row was written. `token` is a credential for *another* service (`core-api`)
 * and the console's only job with it is to hand it to the staff app in a URL
 * fragment and forget it — it is never stored, never shown, never logged.
 */
export const SupportSessionMinted = z.object({
  tenant_id: z.string(),
  tenant_name: z.string(),
  token: z.string().min(1),
  minted_at: z.string(),
  expires_at: z.string(),
  /** The operator's uid, as the row and the token both name it. */
  operator: z.string(),
  /** The `audit_log` row this look is recorded as. */
  audit_id: z.string(),
  read_only: z.literal(true),
  note: z.string(),
});
export type SupportSessionMinted = z.infer<typeof SupportSessionMinted>;

export const ModulesChanged = z.object({
  tenant_id: z.string(),
  modules: z.array(ModuleRow),
  enabled: z.array(z.string()),
  note: z.string(),
});
export type ModulesChanged = z.infer<typeof ModulesChanged>;

/**
 * The house's own offer rules, read back **resolved** (A2–A4).
 *
 * `offer_policy` is `TenantOfferPolicy` from `@yourstore/domain` rather than a
 * restatement of it, for the reason `conciergeSettings` takes the package's own
 * `.strict()` patch type: this document is validated by the same schema on the
 * way in and read by the same one in the engine, and a third spelling of it in a
 * console bundle would be the copy that drifts. Nullable because "this tenant
 * has no policy" and "this tenant's policy is open" are different facts.
 */
export const SchedulingPolicySet = z.object({
  tenant_id: z.string(),
  default_buffer_before_min: z.number(),
  default_buffer_after_min: z.number(),
  min_lead_minutes: z.number(),
  same_day_cutoff: z.string().nullable(),
  offer_policy: TenantOfferPolicy.nullable(),
  note: z.string(),
});
export type SchedulingPolicySet = z.infer<typeof SchedulingPolicySet>;

/**
 * The seven counter settings, read back **resolved** (B4, C1, A5, §2.3, spec §3.6/§6.7).
 *
 * Every field is `@yourstore/domain`'s own schema rather than a restatement of
 * it, for the reason the comment above gives and one this response makes
 * sharper: nothing here is nullable. The server does not answer what is
 * *stored*, it answers what the till, the shop and the guest site will act on —
 * `tillTendersOf` falls back to the fleet's three buttons, `shopPaymentOf`
 * resolves an absent key against whether there is a payment rail at all — so
 * "not configured" is a fact the card derives from the tenant's own `config`
 * document, which it already holds, rather than one this answer could carry.
 */
export const SettingsSet = z.object({
  tenant_id: z.string(),
  till: TillConfig,
  shop_fulfilment: ShopFulfilment,
  shop_payment: ReservationPayment,
  shop_pickup_expiry_days: z.number(),
  order_timing: OrderTiming,
  guest_booking: GuestBooking,
  request_hold_hours: z.number(),
  /**
   * The kitchen's own controls (V1), resolved through `orderingPolicyOf`:
   * `default_prep_minutes` is the one nullable field in this answer, because
   * "nobody set one" is what the kitchen acts on (no time, no text) and not a
   * fallback the reader could invent.
   */
  ordering: z.object({
    order_confirmation: OrderConfirmation,
    default_prep_minutes: z.number().nullable(),
  }),
  /** Who may change what the business sells (B10). Optional: an older admin-api omits it. */
  catalog_editors: CatalogEditors.optional().catch(undefined),
  note: z.string(),
});
export type SettingsSet = z.infer<typeof SettingsSet>;

export const KeyList = z.object({
  tenant_id: z.string(),
  keys: z.array(ApiKeyRow),
  note: z.string(),
});
export type KeyList = z.infer<typeof KeyList>;

/**
 * The one response in the system that carries a live credential.
 *
 * `plaintext` exists here and nowhere else — not in `KeyList`, not in the audit
 * row, not in a log line. The console holds it in component state, shows it in
 * a copy box, and drops it when the box is dismissed; there is no store to put
 * it in and there must not be one.
 */
export const KeyIssued = z.object({
  key_id: z.string(),
  tenant_id: z.string(),
  integration: z.string(),
  env: KeyEnv,
  prefix: z.string(),
  scopes: z.array(z.string()),
  active_slot: z.number().nullable(),
  plaintext: z.string(),
  plaintext_note: z.string(),
  warnings: z.array(z.string()),
});
export type KeyIssued = z.infer<typeof KeyIssued>;

export const KeyRevoked = z.object({
  key_id: z.string(),
  tenant_id: z.string(),
  integration: z.string(),
  env: KeyEnv,
  freed_slot: z.number().nullable(),
  status: z.literal('revoked'),
  effective_within_seconds: z.number(),
});
export type KeyRevoked = z.infer<typeof KeyRevoked>;

export const WebhookList = z.object({
  tenant_id: z.string(),
  endpoints: z.array(WebhookEndpoint),
  note: z.string(),
});
export type WebhookList = z.infer<typeof WebhookList>;

export const WebhookSet = WebhookEndpoint.extend({
  tenant_id: z.string(),
  created: z.boolean(),
  note: z.string(),
});
export type WebhookSet = z.infer<typeof WebhookSet>;

export const DeliveryPage = z.object({
  tenant_id: z.string(),
  deliveries: z.array(Delivery),
  limit: z.number(),
  offset: z.number(),
});
export type DeliveryPage = z.infer<typeof DeliveryPage>;

export const Requeued = z.object({
  tenant_id: z.string(),
  since: z.string(),
  requeued: z.number(),
  created: z.number(),
  endpoints_cleared: z.number(),
  note: z.string(),
});
export type Requeued = z.infer<typeof Requeued>;

export const AuditPage = z.object({
  entries: z.array(AuditEntry),
  limit: z.number(),
  offset: z.number(),
  note: z.string(),
});
export type AuditPage = z.infer<typeof AuditPage>;

/* ------------------------------------------------------------------ */
/* Jobs                                                                */
/* ------------------------------------------------------------------ */

export const JobExecution = z.object({
  execution_id: z.string(),
  name: z.string(),
  /** Derived by the server from the counters, not read from a status field. */
  state: z.enum(['running', 'succeeded', 'failed', 'unknown']),
  created_at: z.string().nullable(),
  started_at: z.string().nullable(),
  completed_at: z.string().nullable(),
  task_count: z.number().nullable(),
  succeeded_count: z.number().nullable(),
  failed_count: z.number().nullable(),
});
export type JobExecution = z.infer<typeof JobExecution>;

export const JobKind = z.enum(['conformance', 'import']);
export type JobKind = z.infer<typeof JobKind>;

export const JobStarted = z.object({
  job: JobKind,
  tenant_id: z.string(),
  operation: z.string(),
  execution: JobExecution.nullable(),
  /** Import only — the server states which kind of run it was (spec §9). */
  execute: z.boolean().optional(),
  dry_run: z.boolean().optional(),
  note: z.string(),
});
export type JobStarted = z.infer<typeof JobStarted>;

export const ExecutionList = z.object({
  job: JobKind,
  executions: z.array(JobExecution),
});
export type ExecutionList = z.infer<typeof ExecutionList>;

export const ExecutionDetail = z.object({
  job: JobKind,
  execution: JobExecution,
});
export type ExecutionDetail = z.infer<typeof ExecutionDetail>;

/* ------------------------------------------------------------------ */
/* The concierge integration                                           */
/* (../../aiconcierge/docs/partner-provisioning-api.md)                */
/* ------------------------------------------------------------------ */

/**
 * aiconcierge's own payload, as this console renders it.
 *
 * **Every field is nullish and nothing here is required but `tenant_id`.** That
 * is a deliberate departure from the rest of this file, which is strict because
 * a missing field would render as an empty cell that reads like a fact. The
 * difference is ownership: the rest of this file mirrors `apps/admin-api`, which
 * is ours and changes when we change it. This mirrors a *partner's* wire, which
 * changes on their release schedule — and a Concierge card that failed to
 * render because they added a field would be a console outage caused by
 * somebody else's deploy.
 *
 * The strictness that matters is on the way **out**, not in: `AgentSettingsPatch`
 * in `@yourstore/concierge-client` is `.strict()`, and it is what keeps the
 * panel from offering to edit the tenant's hours "for the concierge".
 */
export const ConciergePartner = z.object({
  tenant_id: z.string(),
  status: z.string().nullish(),
  provider: z
    .object({
      adapter: z.string().nullish(),
      status: z.string().nullish(),
      detail: z.string().nullish(),
    })
    .nullish(),
  catalog_source: z
    .object({ version: z.number().nullish(), synced_at: z.string().nullish() })
    .nullish(),
  profile_source: z
    .object({ version: z.number().nullish(), synced_at: z.string().nullish() })
    .nullish(),
  settings: z
    .object({
      autonomy: z.string().nullish(),
      autonomous_value_ceiling_cents: z.number().nullish(),
      greeting: z.string().nullish(),
      voice_greeting: z.string().nullish(),
      tone: z
        .object({
          voice: z.string().nullish(),
          formality: z.string().nullish(),
          avoid: z.array(z.string()).nullish(),
        })
        .nullish(),
      voice_mode: z.string().nullish(),
      owner_number: z.string().nullish(),
      guardrails: z.record(z.string(), z.unknown()).nullish(),
      services: z
        .array(
          z.object({
            id: z.string(),
            bookable_by_ai: z.boolean(),
            name: z.string().nullish(),
          }),
        )
        .nullish(),
    })
    .nullish(),
  config_version: z.number().nullish(),
  site_key: z.string().nullish(),
  webhook_url: z.string().nullish(),
  console_url: z.string().nullish(),
  /** The to-do list, in words a console can show. `phone_number` links out. */
  missing: z.array(z.string()).nullish(),
});
export type ConciergePartner = z.infer<typeof ConciergePartner>;

/** Our half: what this deployment and this tenant have locally. */
export const ConciergeLocal = z.object({
  active_keys: z.number(),
  partner_api: z.boolean(),
  webhook_url: z.string().nullable(),
  /** A Secret Manager pointer, which is safe to show. Never the secret. */
  webhook_secret_ref: z.string().nullable(),
  webhook_active: z.boolean(),
  webhook_failing: z.boolean(),
  site_key: z.string().nullable(),
  console_url: z.string().nullable(),
  enabled_at: z.string().nullable(),
});
export type ConciergeLocal = z.infer<typeof ConciergeLocal>;

export const ConciergeRead = z.object({
  tenant_id: z.string(),
  /**
   * **False is a state with a sentence, not an error.** The card renders one
   * line and no button: a broken button is worse than no button.
   */
  configured: z.boolean(),
  missing_config: z.array(z.string()).optional(),
  local: ConciergeLocal,
  concierge: ConciergePartner.nullable(),
  /** Set when the partner is configured but did not answer. */
  unreachable: z.string().nullable().catch(null).default(null),
  note: z.string(),
});
export type ConciergeRead = z.infer<typeof ConciergeRead>;

/**
 * What Enable answers.
 *
 * Note what is **not** on it: the key. The whole point of the action is that
 * the API key and the signing secret go machine to machine, so there is no
 * `plaintext` here and no `OnceBox` to put one in — only the id and the prefix,
 * which are not secrets (K3).
 */
export const ConciergeEnabled = z.object({
  tenant_id: z.string(),
  concierge: ConciergePartner,
  modules_enabled: z.array(z.string()),
  key_id: z.string(),
  key_prefix: z.string(),
  key_env: KeyEnv,
  revoked_key_ids: z.array(z.string()),
  kept_key_ids: z.array(z.string()),
  allowed_origins: z.array(z.string()),
  webhook_url: z.string().nullable(),
  site_key: z.string().nullable(),
  warnings: z.array(z.string()),
  note: z.string(),
});
export type ConciergeEnabled = z.infer<typeof ConciergeEnabled>;

export const ConciergeDisabled = z.object({
  tenant_id: z.string(),
  concierge: ConciergePartner.nullable(),
  revoked_key_ids: z.array(z.string()),
  partner_api_disabled: z.boolean(),
  warnings: z.array(z.string()),
  note: z.string(),
});
export type ConciergeDisabled = z.infer<typeof ConciergeDisabled>;

export const ConciergeChanged = z.object({
  tenant_id: z.string(),
  concierge: ConciergePartner,
  note: z.string().optional(),
});
export type ConciergeChanged = z.infer<typeof ConciergeChanged>;

/* ------------------------------------------------------------------ */
/* The tenant intake (docs/tenant-intake-design.md)                    */
/* ------------------------------------------------------------------ */

export const IntakeStatus = z.enum(['open', 'submitted', 'closed']);
export type IntakeStatus = z.infer<typeof IntakeStatus>;

/**
 * The fields every intake answer carries.
 *
 * `progress` is the **server's**, recomputed from the merged answers on every
 * save (`saveIntakeSection`), and this console never does that arithmetic
 * itself — there is one answer to "how far along is this form" for the same
 * reason there is one scheduling engine. It is `0 / 0` on a link nobody has
 * opened, because progress is computed at the first save and not at the mint;
 * the screen reads that as "not started" rather than printing "0 of 0".
 */
export const IntakeBase = z.object({
  intake_id: z.string(),
  tenant_id: z.string(),
  status: IntakeStatus,
  template: z.string(),
  locale: z.string(),
  schema_version: z.number(),
  progress: z
    .object({
      answered: z.number().catch(0),
      total: z.number().catch(0),
      requiredMissing: z.array(z.string()).catch([]).default([]),
    })
    .catch({ answered: 0, total: 0, requiredMissing: [] }),
  revision: z.number(),
  expires_at: z.string(),
  submitted_at: z.string().nullable(),
  created_by: z.string().catch(''),
  created_at: z.string(),
  updated_at: z.string(),
});

/**
 * What minting answers — **the one response besides a key that carries a live
 * capability.**
 *
 * `code` and `link` exist here and nowhere else: only a SHA-256 is stored, so
 * they cannot be shown again, and re-minting rotates them. They go into an
 * `OnceBox`, which drops them when it is dismissed.
 */
export const IntakeMinted = IntakeBase.extend({
  created: z.boolean(),
  code: z.string(),
  link: z.string(),
  link_note: z.string(),
  host: z.string(),
  host_source: z.string(),
  host_live: z.boolean(),
  modules_enabled: z.array(z.string()).catch([]).default([]),
  warnings: z.array(z.string()).catch([]).default([]),
});
export type IntakeMinted = z.infer<typeof IntakeMinted>;

export const IntakeChanged = IntakeBase;
export type IntakeChanged = z.infer<typeof IntakeChanged>;

export const ReviewPlacement = z.enum(['applied', 'unplaced', 'elsewhere', 'unanswered']);
export type ReviewPlacement = z.infer<typeof ReviewPlacement>;

/**
 * One question, the owner's answer, and where it is going.
 *
 * The **destination chip** and the **placement** are two different facts and
 * the screen shows both: the chip is what the question declares (data in
 * `@yourstore/intake`, true whether or not anybody answered), the placement is
 * what the mapping managed to do with it against this template.
 *
 * `answer` is `unknown` on purpose — a string, a week, a list of rows, a table
 * of quotes. The renderer switches on `kind`, and a schema enumerating the
 * shapes here would be a fourth copy of the question list.
 */
export const ReviewQuestion = z.object({
  question_id: z.string(),
  kind: z.string(),
  ask: z.string(),
  why: z.string().catch(''),
  required: z.boolean().catch(false),
  answered: z.boolean(),
  answer: z.unknown(),
  placement: ReviewPlacement,
  destination: z.string(),
  destination_kind: z.string(),
  where: z.string().nullable(),
  reason: z.string().nullable(),
  detail: z.string().nullable(),
  // The `https:` addresses inside the answer, lifted out by the server so the
  // operator can open a brand's shop link or a review page without hunting
  // through a JSON blob. `.catch([])` because a console one deploy ahead of its
  // API must still render the rest of the row.
  links: z.array(z.string()).catch([]),
});
export type ReviewQuestion = z.infer<typeof ReviewQuestion>;

export const ReviewSection = z.object({
  section_id: z.string(),
  title: z.string(),
  blurb: z.string().catch(''),
  answered: z.number(),
  total: z.number(),
  questions: z.array(ReviewQuestion),
});
export type ReviewSection = z.infer<typeof ReviewSection>;

export const IntakeReview = z.object({
  template: z.string(),
  sections: z.array(ReviewSection),
  counts: z.object({
    applied: z.number(),
    unplaced: z.number(),
    elsewhere: z.number(),
    unanswered: z.number(),
  }),
  answered: z.number(),
  total: z.number(),
});
export type IntakeReview = z.infer<typeof IntakeReview>;

/**
 * The owner's week, as `@yourstore/domain`'s `WeekHours` reaches the wire: a
 * **seven-entry array**, Monday first, each day a list of windows because a
 * restaurant opens twice (spec §4.6). It is mirrored rather than imported for
 * `wire.ts`'s usual reason, and the console renders it and never reasons about
 * it — "is this tenant open at 13:40" is the scheduling engine's question and
 * has exactly one implementation.
 */
export const IntakeDayHours = z.object({
  weekday: z.number(),
  closed: z.boolean().catch(false),
  windows: z
    .array(z.object({ start: z.string(), end: z.string() }))
    .catch([])
    .default([]),
});
export type IntakeDayHours = z.infer<typeof IntakeDayHours>;

export const IntakeWeekHours = z.array(IntakeDayHours);
export type IntakeWeekHours = z.infer<typeof IntakeWeekHours>;

export const IntakeClosure = z.object({
  from: z.string(),
  to: z.string(),
  reason: z.string().catch(''),
  start: z.string(),
  end: z.string(),
});
export type IntakeClosure = z.infer<typeof IntakeClosure>;

export const AppliedClosure = z.object({
  blackout_id: z.string(),
  start: z.string(),
  end: z.string(),
  reason: z.string().catch(''),
});
export type AppliedClosure = z.infer<typeof AppliedClosure>;

export const IntakeRead = IntakeBase.extend({
  tenant_status: TenantStatus,
  tenant_timezone: z.string(),
  /** The owner's own words, whole. Printed as written, never translated. */
  answers: z.record(z.string(), z.unknown()).catch({}),
  review: IntakeReview,
  operator_notes: z.array(
    z.object({
      question_id: z.string(),
      name: z.string(),
      ask: z.string(),
      answer: z.string(),
    }),
  ),
  hours: IntakeWeekHours.nullable(),
  closures: z.array(IntakeClosure),
  applied_closures: z.array(AppliedClosure).catch([]).default([]),
  uploads: z.number(),
  needed_slots: z.array(z.string()).catch([]).default([]),
  modules: z.array(z.string()).catch([]).default([]),
});
export type IntakeRead = z.infer<typeof IntakeRead>;

/**
 * The draft — D5's whole mechanism: a document the editor loads through the
 * same door a template uses. Nothing writes it back as a "draft", because there
 * is no such row and inventing one would be a second versioned document.
 */
export const IntakeDraft = z.object({
  intake_id: z.string(),
  tenant_id: z.string(),
  template: z.string(),
  document: BrandingDocument,
  document_bytes: z.number(),
  warnings: z.array(z.string()),
  applied: z.array(z.object({ question_id: z.string(), where: z.string() })),
  unplaced: z.array(z.object({ question_id: z.string(), reason: z.string(), detail: z.string() })),
  elsewhere: z.array(z.object({ question_id: z.string() })),
  review: IntakeReview,
});
export type IntakeDraft = z.infer<typeof IntakeDraft>;

/**
 * Her menu as a catalog seed file — a **read**, like the branding draft.
 *
 * `needs_operator` is the half that earns the screen: a duration or a price is
 * taken only where her words mean one thing, and everything else arrives here
 * with what she actually said beside it. Nothing in this console writes a
 * catalog; the operator saves the file and runs the CLI.
 */
export const IntakeCatalogDraft = z.object({
  intake_id: z.string(),
  tenant_id: z.string(),
  file: z.unknown(),
  file_text: z.string(),
  needs_operator: z.array(
    z.object({
      row: z.number(),
      ref: z.string(),
      field: z.string(),
      said: z.string().catch(''),
    }),
  ),
  parsable: z.boolean().catch(false),
  command: z.string().catch(''),
});
export type IntakeCatalogDraft = z.infer<typeof IntakeCatalogDraft>;

export const IntakeUpload = z.object({
  upload_id: z.string(),
  slot: z.string(),
  content_type: z.string(),
  bytes: z.number(),
  width: z.number().nullable(),
  height: z.number().nullable(),
  original_name: z.string().catch(''),
  status: z.enum(['planned', 'stored', 'promoted', 'rejected']),
  promoted_url: z.string().nullable(),
  needs_converting: z.boolean(),
  /** A short-lived signed read, minted on this request. `null` with no bucket. */
  view_url: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type IntakeUpload = z.infer<typeof IntakeUpload>;

export const IntakeUploads = z.object({
  intake_id: z.string(),
  tenant_id: z.string(),
  uploads: z.array(IntakeUpload),
  slots: z.array(z.object({ name: z.string(), needed: z.boolean(), max: z.number() })),
  /**
   * **Two flags, because they are two facts.** A deployment may hold the
   * private bucket and no public one: the operator still reviews and rejects,
   * and reads a sentence where Promote would be.
   */
  configured: z.boolean(),
  promotion_configured: z.boolean(),
  missing_config: z.array(z.string()).catch([]).default([]),
});
export type IntakeUploads = z.infer<typeof IntakeUploads>;

export const IntakePromoted = IntakeUpload.extend({
  object: z.string(),
  url: z.string(),
  bytes_in_bucket: z.number(),
  content_type_source: z.string(),
  note: z.string(),
});
export type IntakePromoted = z.infer<typeof IntakePromoted>;

export const HoursApplied = z.object({
  tenant_id: z.string(),
  intake_id: z.string(),
  hours: IntakeWeekHours,
  note: z.string(),
});
export type HoursApplied = z.infer<typeof HoursApplied>;

export const ClosuresApplied = z.object({
  tenant_id: z.string(),
  intake_id: z.string(),
  created: z.number(),
  closures: z.array(
    z.object({
      blackout_id: z.string().nullable(),
      from: z.string(),
      to: z.string(),
      reason: z.string().catch(''),
    }),
  ),
});
export type ClosuresApplied = z.infer<typeof ClosuresApplied>;

export const ClosureRemoved = z.object({ tenant_id: z.string(), blackout_id: z.string() });
export type ClosureRemoved = z.infer<typeof ClosureRemoved>;

/**
 * The fleet's chip: one row per tenant with a live intake, as counts.
 *
 * No answer is on this wire and none could be — `listIntakeSummaries` returns a
 * status, two numbers and a photograph count. The chip means "there is
 * something waiting for you here", which is all a cross-tenant read of somebody
 * else's words is allowed to mean.
 */
export const IntakeSummary = z.object({
  tenant_id: z.string(),
  intake_id: z.string(),
  status: IntakeStatus,
  template: z.string(),
  answered: z.number(),
  total: z.number(),
  uploads: z.number(),
  expires_at: z.string(),
  submitted_at: z.string().nullable(),
  updated_at: z.string(),
});
export type IntakeSummary = z.infer<typeof IntakeSummary>;

export const IntakeSummaryList = z.object({ intakes: z.array(IntakeSummary) });
export type IntakeSummaryList = z.infer<typeof IntakeSummaryList>;

/* ------------------------------------------------------------------ */
/* The catalog (GS4, migration 0050)                                   */
/* ------------------------------------------------------------------ */

/**
 * A localized field, **as stored**.
 *
 * Every other projection in this console takes resolved strings, because its
 * server resolved them for a page. This one does not, and that is the whole
 * difference between rendering a catalog and editing one: an operator who cannot
 * see that a name has Spanish on it will overwrite the Spanish the first time
 * they fix a typo in the English. `repos/admin-catalog.ts` argues it at length.
 */
export const LocalizedField = z.record(z.string(), z.string());
export type LocalizedField = z.infer<typeof LocalizedField>;

export const CatalogImage = z.object({
  id: z.string(),
  position: z.number(),
  url: z.string(),
  alt: LocalizedField.catch({}),
  /** On the website (B10, migration 0072). Absent from an older answer is "on". */
  on_website: z.boolean().catch(true).default(true),
});
export type CatalogImage = z.infer<typeof CatalogImage>;

export const CatalogItem = z.object({
  item_id: z.string(),
  kind: z.string(),
  name: LocalizedField.catch({}),
  description: LocalizedField.catch({}),
  /** `null` is "not in a category", which is not the same as `{}` (0024). */
  category: LocalizedField.nullable().catch(null),
  price_cents: z.number(),
  open_price: z.boolean().catch(false),
  active: z.boolean(),
  /**
   * A product's **Show on the website** (B10, migration 0072); `true` on every
   * other kind, whose switch is `scheduling.bookable_online`.
   */
  on_website: z.boolean().catch(true).default(true),
  sort_order: z.number(),
  scheduling: z
    .object({
      duration_min: z.number(),
      /** `null` is "the house decides" (0064); the card draws the house's number beside it. */
      buffer_before_min: z.number().nullable().catch(null),
      buffer_after_min: z.number().nullable().catch(null),
      max_party: z.number(),
      bookable_by_partner: z.boolean(),
      bookable_online: z.boolean(),
      /**
       * "This one is only ever *added* to another" (0048). The card needs it or
       * it would draw an ordinary-looking service that no route can book and
       * leave an operator hunting a bug that is a flag. It is a **patch** field
       * and never a create one — see `setAttachOnly` on the client.
       */
      attach_only: z.boolean().catch(false),
    })
    .nullable()
    .catch(null),
  retail: z
    .object({ brand: z.string(), notes: z.string(), on_hand: z.number().nullable() })
    .nullable()
    .catch(null),
  menu: z
    .object({ category: LocalizedField.catch({}), sold_out: z.boolean() })
    .nullable()
    .catch(null),
  /**
   * The room type and the trip (`docs/verticals-plan.md` V4), **read-only**
   * on the card — the file is the door, and owner self-service is E3's later
   * design. `null` on a deployment whose console role cannot read the
   * satellite yet (`vertical_satellites` below says which), so the row still
   * draws from its `items` half. The pool and the vessel are ids: 0050 fences
   * `resources` to onboarding including `SELECT`, so a name would abort a live
   * tenant's read.
   */
  stay: z
    .object({
      resource_id: z.string(),
      max_occupancy: z.number(),
      check_in: z.string(),
      check_out: z.string(),
      min_stay_nights: z.number(),
      base_nightly_cents: z.number(),
      bands: z.number().catch(0),
      exceptions: z.number().catch(0),
      bookable_by_partner: z.boolean().catch(true),
      bookable_online: z.boolean().catch(true),
    })
    .nullable()
    .catch(null),
  departure: z
    .object({
      resource_id: z.string(),
      seats: z.number(),
      duration_min: z.number(),
      private: z.boolean().catch(false),
      /** Seven weekdays, Monday first, each a list of `HH:MM` starts. */
      schedule: z.array(z.object({ weekday: z.number(), departures: z.array(z.string()) })),
      season: z.object({ from: z.string(), to: z.string() }).nullable().catch(null),
      bookable_by_partner: z.boolean().catch(true),
      bookable_online: z.boolean().catch(true),
      tiers: z
        .array(
          z.object({
            id: z.string(),
            name: LocalizedField.catch({}),
            price_delta_cents: z.number(),
            sold_out: z.boolean().catch(false),
          }),
        )
        .catch([]),
    })
    .nullable()
    .catch(null),
  images: z.array(CatalogImage).catch([]),
  /**
   * The ways this one is sold (migration 0048), **editable since W5c**.
   *
   * An option's `name` is a plain **string** where the item's is a map, and the
   * asymmetry is deliberate: an item's name usually arrives from a file or an
   * import already carrying two languages and must not be flattened, whereas an
   * option is created here in one language by somebody typing "90 minutes". The
   * multilingual door for options is the catalog file, which is diff-driven, so
   * a bilingual card is authored there rather than retyped into a map editor.
   *
   * `duration_min` is nullable and the null is load-bearing: it says this option
   * **does not change the span** — a dish's *Large* — as against a number, which
   * sets it.
   */
  options: z
    .array(
      z.object({
        id: z.string(),
        ref: z.string(),
        name: z.string(),
        price_cents: z.number(),
        duration_min: z.number().nullable(),
        is_default: z.boolean(),
        active: z.boolean(),
        sort_order: z.number().catch(0),
      }),
    )
    .catch([]),
  /**
   * What may be attached to this one, with the **resolved** attached numbers:
   * dermaplaning is $85 alone and $45 on a facial, and $45 is what the link
   * holds. The server has already coalesced the nullable columns, so both
   * numbers here are facts rather than "or its own".
   */
  add_ons: z
    .array(
      z.object({
        item_id: z.string(),
        name: z.string(),
        price_cents: z.number(),
        duration_min: z.number(),
        active: z.boolean(),
        sort_order: z.number().catch(0),
      }),
    )
    .catch([]),
});
export type CatalogItem = z.infer<typeof CatalogItem>;

/**
 * What an option or add-on write answers with: the counter, and **the item as it
 * now is**.
 *
 * The item rides along because these writes move numbers the caller did not
 * name — marking a new default restates the item's headline price and minutes in
 * the same transaction — so a row that repainted from its own optimism would
 * show the operator a price the catalog no longer holds.
 */
export const CatalogOptionChanged = z.object({
  tenant_id: z.string(),
  item_id: z.string(),
  option_id: z.string().optional(),
  add_on_item_id: z.string().optional(),
  catalog_version: z.number(),
  item: CatalogItem,
});
export type CatalogOptionChanged = z.infer<typeof CatalogOptionChanged>;

/**
 * `editable` is a **privilege** fact (has this deployment applied 0050) and
 * `commerce_editable` is a **status** fact (is this tenant still onboarding).
 * They are two fields because they are two different sentences the card has to
 * say, and neither of them is a permission the console then enforces: every
 * write is allowed or refused by the database when it arrives.
 */
export const TenantCatalog = z.object({
  tenant_id: z.string(),
  editable: z.boolean(),
  commerce_editable: z.boolean().catch(false),
  status: TenantStatus.optional(),
  catalog_version: z.number().catch(0),
  default_locale: z.string().catch('en'),
  /**
   * Whether the console's role could read `item_lodging` and `item_departure`
   * — a privilege fact like `editable`, and false on every deployment until
   * the store owner grants it. The Rooms and Sailings tabs draw either way;
   * this decides whether they carry the calendar and the timetable or a note.
   */
  vertical_satellites: z.boolean().catch(false),
  items: z.array(CatalogItem).catch([]),
  note: z.string().optional(),
  /**
   * The tenant's own gap between guests (A2), so a buffer box left blank can
   * say what blank *means* — "house default (15)". Optional and caught, for
   * the same reason every additive field on this wire is: an `admin-api` one
   * deploy behind answers without it, and the box then says "house default"
   * with no number rather than refusing to draw.
   */
  house_buffers: z
    .object({ before_min: z.number(), after_min: z.number() })
    .optional()
    .catch(undefined),
  /**
   * Whether a photograph can be uploaded from the console on this deployment
   * (both buckets and the public address). Caught to `false`, so an
   * `admin-api` one deploy behind draws the sentence rather than a picker.
   */
  photo_uploads_configured: z.boolean().catch(false),
  photo_uploads_missing: z.array(z.string()).catch([]),
});
export type TenantCatalog = z.infer<typeof TenantCatalog>;

export const CatalogChanged = z.object({
  tenant_id: z.string(),
  item_id: z.string().optional(),
  catalog_version: z.number(),
  images: z.number().optional(),
});
export type CatalogChanged = z.infer<typeof CatalogChanged>;

export const CatalogCategoryRenamed = z.object({
  tenant_id: z.string(),
  from: z.string(),
  to: z.string().nullable(),
  items: z.number(),
  catalog_version: z.number(),
});
export type CatalogCategoryRenamed = z.infer<typeof CatalogCategoryRenamed>;

const SeedCounts = z.object({
  created: z.number().catch(0),
  updated: z.number().catch(0),
  unchanged: z.number().catch(0),
  removed: z.number().catch(0),
});

/**
 * What a seed did, or — when `applied` is false — what it **would** do.
 *
 * There is no second shape for the dry run and there is no second code path
 * behind it: the server runs the real transaction and rolls it back, so these
 * counts are what happened, in a transaction that then did not.
 */
export const CatalogSeeded = z.object({
  tenant_id: z.string(),
  applied: z.boolean(),
  catalog_version: z.number(),
  resources: z.number().catch(0),
  services: z.number().catch(0),
  menu: z
    .object({
      changed: z.boolean(),
      categories: z.number().catch(0),
      items: SeedCounts,
      groups: SeedCounts,
      modifiers: SeedCounts,
    })
    .nullable()
    .catch(null),
  retail: z.object({ changed: z.boolean(), items: SeedCounts }).nullable().catch(null),
  /** How many room types and trips the file described — `services`' siblings. */
  stays: z.number().catch(0),
  departures: z.number().catch(0),
  /** What the run did to them; `null` when the file carried neither half. */
  verticals: z
    .object({ stays: SeedCounts, departures: SeedCounts, tiers: SeedCounts })
    .nullable()
    .catch(null),
  note: z.string().optional(),
});
export type CatalogSeeded = z.infer<typeof CatalogSeeded>;

/**
 * A signed PUT for one photograph from the operator's computer
 * (`routes/catalog-uploads.ts`). `headers` is what the signature was made over,
 * and the PUT sends exactly it.
 */
export const CatalogUploadMinted = z.object({
  tenant_id: z.string(),
  item_id: z.string(),
  upload_id: z.string(),
  upload_url: z.string(),
  method: z.literal('PUT'),
  headers: z.record(z.string(), z.string()),
  expires_at: z.string(),
  max_bytes: z.number(),
});
export type CatalogUploadMinted = z.infer<typeof CatalogUploadMinted>;

/** The landed photograph, copied to the public bucket: the address to attach. */
export const CatalogUploadPublished = z.object({
  tenant_id: z.string(),
  item_id: z.string(),
  upload_id: z.string(),
  url: z.string(),
  bytes: z.number(),
  content_type: z.string(),
});
export type CatalogUploadPublished = z.infer<typeof CatalogUploadPublished>;

/* ------------------------------------------------------------------ */
/* Readiness — the Overview's "getting live" facts (console redesign B1) */
/* ------------------------------------------------------------------ */

/**
 * Where one business is in getting live (`GET …/readiness`,
 * `repos/readiness.ts` in the store). Counts, booleans, short token lists and
 * one instant; the checklist the Overview draws is computed from these in
 * `ui/business/overview/steps.ts`, because which fact makes a step "done"
 * is a presentation judgement, not a server rule.
 *
 * Tolerant where a newer server could say more: an intake status this build
 * does not know reads as "no questionnaire" rather than failing the whole tab.
 */
export const TenantReadiness = z.object({
  tenant_id: z.string(),
  as_of: z.string(),
  status: z.string(),
  catalog: z.object({
    active: z.number(),
    priced: z.number(),
    services: z.number(),
    services_priced: z.number(),
  }),
  modules: z.array(z.string()),
  till_tenders: z.array(z.string()).nullable(),
  open_days: z.number(),
  clients: z.number(),
  staff_seats: z.number(),
  staff_look: z.boolean(),
  site_version: z.number(),
  intake: z
    .object({ status: z.enum(['open', 'submitted', 'closed']), expired: z.boolean() })
    .nullable()
    .catch(null),
  domains: z.object({
    total: z.number(),
    live: z.number(),
    custom: z.number(),
    custom_live: z.number(),
  }),
  concierge: z.boolean(),
  texts: z.object({
    reminders: z.boolean(),
    owner_alerts: z.boolean(),
    owner: z.string(),
  }),
  keys: z.number(),
  last_booking_at: z.string().nullable(),
  note: z.string().optional(),
});
export type TenantReadiness = z.infer<typeof TenantReadiness>;

/* ------------------------------------------------------------------ */
/* Their app's look (console redesign B8)                              */
/* ------------------------------------------------------------------ */

/**
 * The branding read, as the app's look card reads it: the same response plus
 * whether this deployment can take a picture from the operator's computer.
 * Tolerant: a server one deploy behind answers neither, and the card then
 * draws the sentence rather than a file picker.
 */
export const AppLookRead = BrandingRead.extend({
  asset_uploads_configured: z.boolean().catch(false).default(false),
  asset_uploads_missing: z.array(z.string()).catch([]).default([]),
});
export type AppLookRead = z.infer<typeof AppLookRead>;

/** `PUT …/branding/app-look` — the staff document alone; the website's version is untouched. */
export const AppLookSaved = z.object({
  tenant_id: z.string(),
  branding_staff: StaffBrandingDocument.nullable().catch(null).default(null),
  branding_staff_accent_soft: z.string().nullable().catch(null).default(null),
  branding_version: z.number(),
  effective: z.string(),
});
export type AppLookSaved = z.infer<typeof AppLookSaved>;

/** A signed PUT for one business-level picture (`routes/tenant-assets.ts`). */
export const TenantAssetMinted = z.object({
  tenant_id: z.string(),
  kind: z.string(),
  upload_id: z.string(),
  upload_url: z.string(),
  method: z.literal('PUT'),
  headers: z.record(z.string(), z.string()),
  expires_at: z.string(),
  max_bytes: z.number(),
});
export type TenantAssetMinted = z.infer<typeof TenantAssetMinted>;

/** The landed picture, copied to the public bucket: the address the look's save hangs on. */
export const TenantAssetPublished = z.object({
  tenant_id: z.string(),
  kind: z.string(),
  upload_id: z.string(),
  url: z.string(),
  bytes: z.number(),
  content_type: z.string(),
});
export type TenantAssetPublished = z.infer<typeof TenantAssetPublished>;

/**
 * **What a publish would say, before it is one** — `POST …/branding/check`
 * (console redesign C2). The publish's own validator over the builder's
 * document: the warnings it would publish with and what the sanitizer would
 * take out of each content zone. Nothing is stored; a refusal is the
 * publish's `400`, word for word.
 */
export const BrandingChecked = z.object({
  tenant_id: z.string(),
  ok: z.boolean(),
  warnings: z.array(z.string()),
  zones: z.array(ZoneReport),
  document_bytes: z.number(),
});
export type BrandingChecked = z.infer<typeof BrandingChecked>;

/* ------------------------------------------------------------------ */
/* Who can sign in — a business's staff logins                         */
/* ------------------------------------------------------------------ */

/** The three roles a staff login can hold — the ones `core-api` enforces. */
export const LOGIN_ROLES = ['owner', 'staff', 'readonly'] as const;
export type LoginRole = (typeof LOGIN_ROLES)[number];

/**
 * One login as `GET …/logins` answers it: the record and Identity Platform
 * side by side (`@yourstore/identity`'s `mergeLogins`). Tolerant where a newer
 * server could say more: an unknown flag is kept as a string, and an unknown
 * role or state reads as the plainest one, so the card still draws.
 */
export const StaffLogin = z.object({
  uid: z.string(),
  email: z.string(),
  name: z.string().catch(''),
  role: z.enum(LOGIN_ROLES).nullable().catch(null),
  state: z.enum(['active', 'disabled', 'removed']).catch('active'),
  recorded: z.boolean(),
  has_login: z.boolean(),
  last_sign_in_at: z.string().nullable().catch(null),
  flags: z.array(z.string()).catch([]),
});
export type StaffLogin = z.infer<typeof StaffLogin>;

export const LoginList = z.object({
  tenant_id: z.string(),
  logins: z.array(StaffLogin),
  scanned: z.number(),
  truncated: z.boolean(),
});
export type LoginList = z.infer<typeof LoginList>;

/**
 * `POST …/logins`. `invite_email: "failed"` is a success: the login is made and
 * carries its business, and Google did not send the set-your-password email —
 * the card says so and offers Resend. No link, code or password is ever in
 * this answer; none exists on our side to put there.
 */
export const LoginInvited = z.object({
  tenant_id: z.string(),
  login: z.object({ uid: z.string(), email: z.string() }),
  created: z.boolean(),
  invite_email: z.enum(['sent', 'failed']),
  invite_email_problem: z.string().optional(),
});
export type LoginInvited = z.infer<typeof LoginInvited>;

/** Resend, change role, remove access, record it — each answers the login it touched. */
export const LoginChanged = z.object({ tenant_id: z.string(), uid: z.string() });
export type LoginChanged = z.infer<typeof LoginChanged>;
